package one.healthium.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import one.healthium.domain.common.ReviewItem
import one.healthium.domain.common.TextStyle
import one.healthium.ui.common.utils.toColor
import one.healthium.ui.common.utils.toTextAlign
import one.healthium.ui.common.utils.toTextStyle
import one.healthium.ui.compose.components.AppPageIndicator
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import one.healthium.ui.compose.theme.gray_6

@Composable
fun ReviewsPager(
    modifier: Modifier = Modifier,
    reviews: List<ReviewItem>,
    authorTextStyle: TextStyle? = null,
    reviewTextStyle: TextStyle? = null,
) {
    if (reviews.isEmpty()) return

    val pagerState = rememberPagerState(
        pageCount = { reviews.size }
    )
    LaunchedEffect(Unit) {
        while (true) {
            delay(2500)
            val nextPage = if (pagerState.currentPage + 1 < pagerState.pageCount) {
                pagerState.currentPage + 1
            } else {
                0
            }
            pagerState.animateScrollToPage(nextPage)
        }
    }
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        MeasureMaxHeight(
            measureCount = pagerState.pageCount,
            viewToMeasure = {
                ReviewPage(reviews[it], authorTextStyle, reviewTextStyle)
            },
        ) { maxHeight ->
            HorizontalPager(
                modifier = Modifier.fillMaxWidth(),
                state = pagerState,
                userScrollEnabled = false,
                verticalAlignment = Alignment.Top,
            ) { page ->
                Box(Modifier.height(maxHeight)) {
                    ReviewPage(reviews[page], authorTextStyle, reviewTextStyle)
                }
            }
        }
        Spacer(Modifier.height(16.dp))
        AppPageIndicator(
            size = 6.dp,
            activeColor = gray_3,
            inactiveColor = gray_6,
            pages = pagerState.pageCount,
            currentPage = pagerState.currentPage,
        )
    }
}

@Composable
private fun ReviewPage(
    model: ReviewItem,
    authorTextStyle: TextStyle?,
    reviewTextStyle: TextStyle?,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 48.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(text = model.rating ?: "⭐️⭐️⭐️⭐️⭐️")
        Spacer(Modifier.height(16.dp))
        if (!model.review.isNullOrBlank()) {
            Text(
                modifier = Modifier.fillMaxWidth(),
                text = model.review!!,
                style = reviewTextStyle.toTextStyle(AppTheme.textStyles.body1Medium),
                color = reviewTextStyle?.color?.toColor() ?: AppTheme.colors.text100,
                textAlign = reviewTextStyle?.alignment.toTextAlign() ?: TextAlign.Center,
            )
        }
        if (!model.author.isNullOrBlank()) {
            Text(
                modifier = Modifier.fillMaxWidth(),
                text = model.author!!,
                style = authorTextStyle.toTextStyle(AppTheme.textStyles.body1Medium),
                color = authorTextStyle?.color?.toColor() ?: gray_3,
                textAlign = authorTextStyle?.alignment.toTextAlign() ?: TextAlign.Center,
            )
        }
    }
}
